import Base from './Base';
import { cloneDeep, debounce } from 'lodash';
import Contact from './Contact';
import User from './User';
import Phone from './Phone';
import moment from 'moment';
import user from '../modules/user';
import MessageSimple from './MessageSimple';
import getInboxType from '../../utils/getInboxType';
import { useConversationsStore, useMessagesStore } from 'store/modules';

export default class Conversation extends Base {
    constructor(item) {
        super(item);

        if (item.contact) {
            this.contact = new Contact(item.contact);
        }

        this.participants = item.participants
            ? item.participants.map((item) => new Contact(item))
            : [];

        if (this.owner) {
            this.owner = new User(this.owner);
        }

        if (item.number) {
            this.number = new Phone(item.number);
        }

        if (user.state.user) {
            this.userOwner = user.state.user;
        }

        if (item.recent_message) {
            this.recent_message = new MessageSimple(item.recent_message);
        }

        if (item.draft_message) {
            this.draft_message = item.draft_message.message;
        }

        this.favorite_for_users = Array.isArray(item.favorite_for_users)
            ? item.favorite_for_users
            : [];
        this.has_new_message = Boolean(item.has_new_message);
        this.hasCurrentSocket = true;
        this.draftInitializedForm = true;
        this.debounceNotifyDrafting = debounce(this.notifyDrafting, 4000);
    }

    initNotifyDraftingCancel = () => {
        this.debounceNotifyDrafting.cancel();
    };

    initNotifyDrafting = (value, _this) => {
        this.debounceNotifyDrafting.cancel();
        this.debounceNotifyDrafting(value, _this);
    };

    initDraftCurrentSocket = async () => {
        await useConversationsStore().updateOrAddConversation({
            conversation: {
                id: this.id,
                hasCurrentSocket: true,
            },
            updateOrder: false,
            updateProps: true,
        });
    };

    initDraftWait = async (_this) => {
        await useConversationsStore().updateOrAddConversation({
            conversation: {
                id: this.id,
                draftInitializedForm: false,
                has_new_message: false,
            },
            updateOrder: false,
            updateProps: true,
        });

        if (this.draft_message) {
            await _this.updateDraftForm(cloneDeep(this.draft_message));
        }
    };

    initDraftForm = async (_this) => {
        await useConversationsStore().updateOrAddConversation({
            conversation: {
                id: this.id,
                draftInitializedForm: false,
            },
            updateOrder: false,
            updateProps: true,
        });

        if (this.draft_message) {
            await _this.updateDraftForm(cloneDeep(this.draft_message));
            await _this.$delay(1000);
        } else if (this.origin_draft) {
            await _this.updateDraftForm(cloneDeep(this.origin_draft.message));
            await _this.$delay(1000);
        }

        await useConversationsStore().updateOrAddConversation({
            conversation: {
                id: this.id,
                draftInitializedForm: true,
            },
            updateOrder: false,
            updateProps: true,
        });
    };

    updateDraftForm = (_this) => {
        if (this.draft_message) {
            _this.updateDraftForm(cloneDeep(this.draft_message));
        } else {
            _this.resetDraftForm(this.id);
        }
    };

    notifyDrafting = async (messageData, _this) => {
        const message = cloneDeep(messageData);
        const privateChannel = _this.$echo.channel(`private-user.${this.userOwner.id}`);

        if (privateChannel && privateChannel.whisper) {
            let id = this.id;

            if (this.id === 0) {
                const contactsIds = this.dataContacts.map((item) => item.id);
                id = `${this.inbox_id}_${contactsIds.join('_')}_new`;
            }

            privateChannel.whisper('draft', {
                id: this.userOwner.id,
                conversation_id: id,
                message: message,
                contacts: this.dataContacts.map((item) => item.dataDraft),
                inbox_id: this.inbox_id,
                inbox_type: getInboxType(this.inbox_id),
                socketId: _this.$echo.socketId(),
            });

            await useConversationsStore().updateOrAddConversation({
                conversation: {
                    id: this.id,
                    draft_message: {
                        message: message,
                    },
                    hasCurrentSocket: true,
                },
                updateOrder: false,
                updateProps: true,
            });
        }
    };

    deleteOriginMessageDraft = async () => {
        if (!this.origin_draft) return;

        this.debounceNotifyDrafting.cancel();

        const params = {
            conversation_id: this.origin_draft.conversation_id,
            inbox_id: this.origin_draft.inbox_id,
            inbox_type: getInboxType(this.origin_draft.inbox_id),
        };

        await useMessagesStore().deleteMessagesDraft({
            isAxios: true,
            params: params,
        });
    };

    deleteMessageDraft = async (_this) => {
        this.debounceNotifyDrafting.cancel();

        if (!this.draft_message) return;

        const params = {
            conversation_id: this.id,
            inbox_id: this.inbox_id,
            inbox_type: getInboxType(this.inbox_id),
        };
        await useConversationsStore().updateOrAddConversation({
            conversation: {
                id: this.id,
                draftInitializedForm: false,
            },
            updateOrder: false,
            updateProps: true,
        });
        await useMessagesStore().deleteMessagesDraft({
            isAxios: true,
            params: params,
        });
        await _this.resetDraftForm(this.id);
        await _this.$delay(300);
        await useConversationsStore().updateOrAddConversation({
            conversation: {
                id: this.id,
                draftInitializedForm: true,
            },
            updateOrder: false,
            updateProps: true,
        });
    };

    get hasDraftMessage() {
        return Boolean(!this.has_new_message && this.draft_message);
    }

    get messageDraftBody() {
        if (!this.draft_message) return '';
        if (!this.draft_message.message) return '';

        if (this.draft_message.message.length > 100) {
            return `${this.draft_message.message.slice(0, 100)}...`;
        }

        return this.draft_message.message.slice(0, 100);
    }

    get unreadCount() {
        if (this.unread_count) return this.unread_count;

        if (this.conversationIsUnread) return 1;

        return 0;
    }

    get conversationIsUnreadCount() {
        return Boolean(this.conversationIsUnread || this.unread_count);
    }

    get conversationIsUnread() {
        if (this.id === 0) {
            return false;
        }

        if (!this.last_read_at) {
            return true;
        }

        return this.last_read_at < this.last_message_at;
    }

    get conversationIsTagging() {
        if (this.id === 0) {
            return false;
        }

        if (!this.has_tagging) {
            return false;
        }

        if (!this.tagging_last_message_at) {
            return false;
        }

        if (!this.tagging_last_read_at) {
            return true;
        }

        return this.tagging_last_read_at < this.tagging_last_message_at;
    }

    get conversationIsUnassigned() {
        return !this.user_id;
    }

    get conversationClosedPretty() {
        return `Closed ${window.moment.utc(this.closed_at).local().format('MMM Do')}`;
    }

    update(item, exclude = [], updateProps) {
        let data = {};

        if (!Array.isArray(updateProps) && typeof updateProps === 'object') {
            for (let key in updateProps) {
                this[key] = updateProps[key];
            }

            return this;
        }

        if (Array.isArray(updateProps)) {
            for (let key in updateProps) {
                const value = updateProps[key];

                data[value] = item[value];
            }
        } else {
            data = item;
        }

        data = cloneDeep(data);

        for (let key in data) {
            if (!exclude.includes(key)) {
                switch (key) {
                    case 'contact': {
                        if (this.contact) {
                            this.contact = this.contact.update(data[key]);
                        } else {
                            this.contact = new Contact(data[key]);
                        }

                        break;
                    }
                    case 'draft_message': {
                        this.draft_message = data[key]
                            ? typeof data[key].message === 'string'
                                ? data[key]
                                : data[key].message
                            : null;

                        break;
                    }
                    case 'recent_message': {
                        this.recent_message = this.recent_message
                            ? this.recent_message.update({
                                  body: data[key].body,
                                  status: data[key].status,
                                  sent_at: data[key].sent_at,
                                  has_tagging: data[key].has_tagging,
                              })
                            : data[key]
                              ? new MessageSimple(data[key])
                              : null;

                        break;
                    }
                    case 'is_favorite': {
                        this.is_favorite =
                            data['is_favorite'] === null
                                ? data.favorite_for_users.includes(this.userOwner.id)
                                : data['is_favorite'];

                        break;
                    }
                    default:
                        this[key] = data[key];
                }
            }
        }

        return this;
    }

    get data() {
        return {
            id: this.id,
            contact: this.contact.data,
        };
    }

    inboxName(inboxes) {
        if (this.id === 0) return '';

        if (!inboxes[this.inbox_id] && this.inbox_id !== 0) {
            return 'Unknow Inbox';
        }

        if (this.inbox_id) {
            return inboxes[this.inbox_id].name;
        }

        return '';
    }

    getInbox(inboxes) {
        if (this.id === 0) return null;

        if (!inboxes[this.inbox_id] && this.inbox_id !== 0) {
            return null;
        }

        if (this.inbox_id) {
            return inboxes[this.inbox_id];
        }

        return null;
    }

    inboxIsAircall(inboxes) {
        if (this.id === 0) return null;

        if (!inboxes[this.inbox_id] && this.inbox_id !== 0) {
            return null;
        }

        if (this.inbox_id) {
            return inboxes[this.inbox_id].isAircallNumber;
        }

        return null;
    }

    inboxColor(inboxes) {
        if (!inboxes[this.inbox_id]) {
            return 'rgb(99, 114, 128)';
        }

        if (this.inbox_id) {
            return inboxes[this.inbox_id].color;
        }

        return 'rgb(99, 114, 128)';
    }

    updateParticipants(contact, props) {
        this.updateContact(contact, props);

        this.participants = this.participants.map((item) => {
            if (contact.id === item.id) {
                if (props) {
                    return contact.update(props);
                }

                return contact;
            }

            return item;
        });
    }

    updateContact(contact, props) {
        if (contact.id === this.contact?.id) {
            if (props) {
                return this.contact.update(props);
            }

            this.contact = contact;
        }
    }

    get lastMessageAt() {
        const timeNow = moment.utc(this.last_message_at).local().locale('en-short').fromNow();

        if (timeNow === 'a few seconds ago') {
            return 'Seconds ago';
        }

        return timeNow;
    }

    get hasParticipants() {
        if (!this.participants) return false;

        return Boolean(this.participants.length);
    }

    get hasGroup() {
        if (!this.participants) return false;

        return Boolean(this.participants.length);
    }

    get hasOneParticipants() {
        if (!this.participants) return true;

        return this.participants.length <= 1;
    }

    get firstParticipant() {
        if (!this.participants) return false;
        if (!this.participants.length) return false;

        return this.participants[0];
    }

    get fullNameParticipants() {
        if (!this.participants) return '';
        if (this.group_name) return this.group_name;

        return this.fullNameContacts;
    }

    get fullNameContacts() {
        const contacts = [...this.participants, this.contact];

        const fullName = contacts.map((item) => {
            return item.full_name ? item.full_name : item.formatted_number;
        });

        return fullName.join(', ');
    }

    get formattedNumberParticipants() {
        if (!this.participants) return '';

        const contacts = [this.contact, ...this.participants];

        const formattedNumber = contacts.map((item) => {
            return item.full_name ? item.formatted_number : '';
        });

        return formattedNumber.length ? formattedNumber.join(', ') : 'No Name';
    }

    get dataParticipants() {
        if (!this.participants) return [];

        return this.participants.map((item) => item.id);
    }

    get dataContacts() {
        const participants = this.participants || [];
        const contacts = [...participants, this.contact];

        return contacts.sort((a, b) => a.id - b.id);
    }

    get hasPreview() {
        return this.id === 0;
    }

    get hasStarted() {
        return Boolean(this.started_at);
    }

    get hasClosed() {
        return Boolean(this.closed_at);
    }

    get color() {
        const colors = [
            '#6372A5',
            '#1D95F2',
            '#10D0AD',
            '#E13466',
            '#FFC422',
            '#FF9423',
            '#A233E1',
        ];

        return colors[+this.id % colors.length];
    }

    get hasAssignedFilter() {
        return Boolean(this.hasStarted && this.userOwner.id === this.user_id);
    }

    get hasUnassignedFilter() {
        return this.user_id === 0;
    }

    get hasOptOutFilter() {
        return Boolean(this.contact.hasOptOut || this.contact.hasOptOutCurrentInbox);
    }

    get hasOpenFilter() {
        if (this.hasStarted && this.contact.hasOptIn && !this.hasClosed) return true;
        if (this.hasStarted && this.contact.hasOptInCurrentInbox && !this.hasClosed) return true;

        return false;
    }

    get hasBlockedFilter() {
        return this.contact.isBlocked;
    }

    get isSent() {
        if (!this.recent_message || this.draft_message) return false;

        return this.recent_message.sent_at.length > 0;
    }

    get isFailed() {
        if (!this.recent_message) return false;

        return ['failed', 'undelivered'].includes(this.recent_message.status);
    }

    get isVoiceMail() {
        return this.recent_message.is_voicemail;
    }

    get voiceMailText() {
        if (this.recent_message.status === 'transcript') return this.recent_message.body;

        return 'You have a new voicemail';
    }
}
